import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Search } from "lucide-react";

// Fix default marker icons (webpack) via CDN
const defaultIcon = L.icon({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const ZEELAND = [51.4988, 3.6136];

export const MapPicker = ({ value, onChange }) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const containerRef = useRef(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current).setView(value?.lat ? [value.lat, value.lng] : ZEELAND, 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);

    const placeMarker = (lat, lng) => {
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      } else {
        markerRef.current = L.marker([lat, lng], { icon: defaultIcon, draggable: true }).addTo(map);
        markerRef.current.on("dragend", (e) => {
          const p = e.target.getLatLng();
          onChange({ lat: p.lat, lng: p.lng });
        });
      }
    };

    if (value?.lat) placeMarker(value.lat, value.lng);

    map.on("click", (e) => {
      placeMarker(e.latlng.lat, e.latlng.lng);
      onChange({ lat: e.latlng.lat, lng: e.latlng.lng });
    });

    mapRef.current = map;
    // Ensure correct sizing after mount
    setTimeout(() => map.invalidateSize(), 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`,
        { headers: { "Accept-Language": "nl" } }
      );
      const data = await res.json();
      if (data && data[0]) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        mapRef.current.setView([lat, lng], 15);
        if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
        else {
          markerRef.current = L.marker([lat, lng], { icon: defaultIcon, draggable: true }).addTo(mapRef.current);
          markerRef.current.on("dragend", (ev) => {
            const p = ev.target.getLatLng();
            onChange({ lat: p.lat, lng: p.lng, address: query });
          });
        }
        onChange({ lat, lng, address: data[0].display_name });
      }
    } catch (err) {
      console.error("Adres zoeken mislukt:", err);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
          data-testid="map-search-input"
          placeholder="Zoek een adres of plaats..."
          className="flex-1 border-2 border-[#0D3B4C] px-4 py-3 text-base focus:border-[#B71C1C] focus:outline-none"
        />
        <button
          type="button"
          onClick={search}
          data-testid="map-search-btn"
          disabled={searching}
          className="w-full sm:w-auto bg-[#1B6B2E] text-white font-[700] px-6 py-3 text-base flex items-center justify-center gap-2 hover:bg-[#144F22] transition-colors disabled:opacity-50"
        >
          <Search size={20} /> {searching ? "Zoeken..." : "Zoek"}
        </button>
      </div>
      <div
        ref={containerRef}
        data-testid="map-picker"
        className="w-full h-[300px] sm:h-[360px] border-2 border-[#E8F5E9] z-0"
      />
      <p className="text-sm text-[#0D3B4C] mt-2">
        Tik op de kaart of versleep de pin om de exacte plek te kiezen.
      </p>
    </div>
  );
};
